import UserCustomSignatureParams from "@/domain/entities/user/UserCustomSignature";
import { userSignatureToMailvistaMapper } from "@/mappers/user/userSignatureMapper";
import { useUserSignatures } from "@cs_teamwaydev/mailvista-sdk_react";
import { useMutation, UseMutationResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import useErrorToast from "../utils/useErrorToast";
import { useToast } from "../utils/useToast";

const useUpdateUserSignature = (): {
    updateUserSignature: UseMutationResult<
        void,
        Error,
        UserCustomSignatureParams
    >;
} => {
    const userSignatures = useUserSignatures();
    const showErrorToast = useErrorToast();
    const { toast } = useToast();
    const { t } = useTranslation();

    const updateUserSignatureFn = async ({
        id,
        userId,
        isFavorite,
        avatarURL,
        name,
        email,
        phone,
        jobTitle,
        companyName,
        website,
        other,
    }: UserCustomSignatureParams): Promise<void> => {
        const signatureParams = new UserCustomSignatureParams({
            id,
            userId,
            isFavorite,
            avatarURL,
            name,
            jobTitle,
            email,
            companyName,
            phone,
            website,
            other,
        });

        const signatureMailvista =
            userSignatureToMailvistaMapper(signatureParams);

        try {
            await userSignatures.updateUserSignature(signatureMailvista);

            toast({
                description: t("settings.signature.form.saveSuccess"),
                variant: "success",
            });
        } catch (error) {
            console.error(error);
            showErrorToast({
                titleTranslationKey: "errors.signature.update",
            });
        }
    };

    const updateUserSignature = useMutation({
        mutationFn: (signatureParams: UserCustomSignatureParams) =>
            updateUserSignatureFn(signatureParams),
    });

    return {
        updateUserSignature,
    };
};

export default useUpdateUserSignature;
